import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { CssBaseline, PaletteMode } from '@mui/material';
import { Theme, ThemeProvider } from '@mui/material/styles';
import { MUITheme } from '../mui/theme/index';
import { THEME_KEY } from '../const/theme';

export interface IAppThemeContextType {
  theme: Theme;
  mode: PaletteMode;
  dark: boolean;
  toggleMode: () => void;
}

export const AppThemeContext = React.createContext<IAppThemeContextType | null>(
  null
);

interface IAppThemeProvider {
  children?: React.ReactNode;
}

const AppThemeProvider: React.FC<IAppThemeProvider> = ({ children }) => {
  const router = useRouter();
  const { locale = 'en' } = router;

  const [mode, setMode] = useState<PaletteMode>('light');
  const [dark, setDark] = useState<boolean>(mode === 'dark');

  const theme = React.useMemo(
    () => MUITheme.createTheme(mode, locale),
    [mode, locale]
  );

  const toggleMode = () => {
    let theme: PaletteMode;
    if (mode === 'light') theme = 'dark';
    else theme = 'light';
    setMode(theme);
    window.localStorage.setItem(THEME_KEY, theme);
  };

  useEffect(() => {
    const themeStorage = window.localStorage.getItem(THEME_KEY);
    if (themeStorage === 'light' || themeStorage === 'dark')
      setMode(themeStorage);
  }, []);

  useEffect(() => {
    setDark(mode === 'dark');
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode, locale]);

  return (
    <AppThemeContext.Provider value={{ mode, theme, dark, toggleMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </AppThemeContext.Provider>
  );
};

export default AppThemeProvider;
